import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { CheckCircle2, MapPin, FileText, Mail, ArrowRight } from "lucide-react";

/**
 * Registration Confirmation.
 *
 * Shown immediately after a Prime Supplier finishes Step 5 of onboarding.
 * The purpose of this page is to set clear expectations about the
 * QuickBooks invoicing workflow so the supplier does not think registration
 * is "done" the moment they click Finish.
 */
export default function RegistrationComplete() {
  const [exhibitor, setExhibitor] = useState(null);
  const [station, setStation] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [meRes, stnRes] = await Promise.all([
          api.get("/exhibitors/me"),
          api.get("/exhibitors/me/station"),
        ]);
        setExhibitor(meRes.data.exhibitor);
        setStation(stnRes.data.station);
      } catch { nav("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [nav]);

  if (loading) {
    return <Layout><section className="container-summit py-24"><div className="overline">Loading…</div></section></Layout>;
  }

  const stationLabel = station?.label || exhibitor?.booth_number || "—";
  const price = 2500;

  return (
    <Layout>
      <section className="container-summit py-14 md:py-20 max-w-3xl">
        {/* Hero */}
        <div className="text-center" data-testid="reg-complete-hero">
          <div className="inline-flex items-center justify-center h-14 w-14 border-2 border-emerald-500 rounded-full mb-6">
            <CheckCircle2 size={26} className="text-emerald-500" strokeWidth={2} />
          </div>
          <div className="overline mb-4"><span className="accent-rule" />Registration Received</div>
          <h1 className="serif text-4xl sm:text-5xl">Thank you.</h1>
          <p className="mt-5 text-zinc-600 leading-relaxed max-w-xl mx-auto">
            Your Prime Supplier registration has been received. Display America Accounting
            will send the $2,500 Supplier Registration Fee invoice separately.
          </p>
        </div>

        {/* Summary card */}
        <div className="mt-12 border border-black bg-white" data-testid="reg-complete-summary">
          <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-zinc-200">
            <SummaryRow icon={MapPin} label="Reserved Supplier Station" value={stationLabel} testId="rc-station" />
            <SummaryRow icon={FileText} label="Supplier Package" value={`$${price.toLocaleString()}.00`} testId="rc-package" />
          </div>
          <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-zinc-200 border-t border-zinc-200">
            <SummaryRow
              icon={StatusIcon}
              label="Registration Status"
              value={<span className="inline-flex items-center gap-2 text-amber-700"><span className="inline-block w-2 h-2 rounded-full bg-amber-500" />Awaiting Invoice</span>}
              testId="rc-status"
            />
            <SummaryRow icon={Mail} label="Confirmation Sent To" value={exhibitor?.email || "—"} testId="rc-email" />
          </div>
        </div>

        {/* Next steps */}
        <div className="mt-12" data-testid="reg-complete-next-steps">
          <div className="overline mb-5"><span className="accent-rule" />Next Steps</div>
          <ol className="grid gap-4">
            <NextStep n="1" title="Display America Accounting issues your invoice"
              body={`DA Accounting will email your $${price.toLocaleString()} Supplier Registration Fee invoice to ${exhibitor?.email || "your email"} within one business day.`} />
            <NextStep n="2" title="Review and pay"
              body="Use the pay link inside the invoice email. Payment is accepted by credit card or ACH bank transfer. Your Supplier Station stays reserved until payment is complete — there is no time limit." />
            <NextStep n="3" title="Registration confirmed"
              body="Once DA Accounting records your payment, your Supplier Station is permanently assigned, your Dashboard unlocks the next production steps (logo, brand assets, artwork proof), and you'll receive a signed confirmation email." />
          </ol>
        </div>

        {/* Support + CTA */}
        <div className="mt-12 pt-10 border-t border-zinc-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-zinc-500">
            Questions? Email <a href="mailto:info@displayamerica.com" className="text-black underline decoration-[#A39171] underline-offset-4">info@displayamerica.com</a>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/documents" className="btn-outline-black h-12 px-5 text-xs inline-flex items-center gap-2" data-testid="rc-view-documents">
              <FileText size={14} /> Event Documents
            </Link>
            <Link to="/dashboard" className="btn-black h-12 px-6" data-testid="rc-continue">
              Continue to Supplier Dashboard <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function SummaryRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="p-6 md:p-7 flex items-start gap-4" data-testid={testId}>
      <div className="h-9 w-9 shrink-0 border border-black flex items-center justify-center">
        <Icon size={15} strokeWidth={1.6} />
      </div>
      <div className="min-w-0">
        <div className="overline">{label}</div>
        <div className="serif text-2xl mt-1 truncate">{value}</div>
      </div>
    </div>
  );
}

// Distinct component used for the status row so its icon inherits the amber tone.
function StatusIcon(props) {
  return <FileText {...props} />;
}

function NextStep({ n, title, body }) {
  return (
    <li className="flex items-start gap-5 p-5 border border-zinc-200 bg-white">
      <div className="serif text-3xl text-[#A39171] tabular-nums leading-none shrink-0">{n}</div>
      <div>
        <div className="serif text-lg leading-tight">{title}</div>
        <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed">{body}</p>
      </div>
    </li>
  );
}
